import { useCallback, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../constants/api';


export const useUserDetailsApi = () => {

	const [baselinePackageCost, setBaselinePackageCost] = useState<number>(0);
	const [perSeatCost, setPerSeatCost] = useState<number>(0);
	const [vatRate, setVatRate] = useState<number>(0);
	const [agency, setAgency] = useState<any | null>(null); // Adjusted type for agency state

	// Update user profile details
	const updateUserProfile = useCallback(async (profile: { 
		surname?: string; 
		fullNames?: string; 
		nickname?: string;
		identificationType?: string;
		idNumber?: string;
		dateOfBirth?: Date;
		gender?: string;
		email?: string;
		mobileNumber?: string;
		landlineNumber?: string;
		// Physical address fields
		unitNumber?: string;
		complex?: string;
		streetNumber?: string;
		streetName?: string;
		suburb?: string;
		city?: string;
		postalCode?: string;
		// Postal address fields
		postalSameAsResidential:boolean,
		postalUnitNumber?: string,
		postalComplex?: string,
		postalStreetNumber?: string;
		postalStreetName?: string;
		postalSuburb?: string;
		postalCity?: string;
		postalPostalCode?: string;
	}) => {
		const token = sessionStorage.getItem('token');
		const response = await axios.put(`${API_BASE_URL}/api/auth/update-profile`, profile, {
			headers: { Authorization: `Bearer ${token}` }
		});
		if (response.data.user) {
			return response.data.user;
		}
		throw new Error('No user returned');
	}, []);

	// Update agency details
	const updateAgencyDetails = useCallback(async (agencyId: string, agencyData: any) => {
		const token = sessionStorage.getItem('token');
		const response = await axios.put(`${API_BASE_URL}/api/agency/${agencyId}/update`, agencyData, {
			headers: { Authorization: `Bearer ${token}` }
		});
		if (response.data.agency) {
			return response.data.agency;
		}
		throw new Error('No agency returned');
	}, []);

	// Send user invitation
	const sendUserInvitation = useCallback(async (inviteData: { email: string; agencyId: string; role: string; senderEmail: string }) => {
		const token = sessionStorage.getItem('token');
		const response = await axios.post(`${API_BASE_URL}/api/invitations/send`, inviteData, {
			headers: { Authorization: `Bearer ${token}` }
		});
		return response.data;
	}, []);

	// Remove users from agency
	const removeAgencyUsers = useCallback(async (agencyId: string, userIds: string[], userId: string) => {
		const token = sessionStorage.getItem('token');
		const response = await axios.post(`${API_BASE_URL}/api/agency/${agencyId}/remove-users`, {
			userIds,
			agencyId,
			userId
		}, {
			headers: { Authorization: `Bearer ${token}` }
		});
		return response.data;
	}, []);

	// Fetch agency users
	const fetchAgencyUsers = useCallback(async (agencyId: string) => {
		const token = sessionStorage.getItem('token');
		const response = await axios.get(`${API_BASE_URL}/api/agency/${agencyId}/users`, {
			headers: { Authorization: `Bearer ${token}` }
		});
		return response.data;
	}, []);

	const fetchPendingAgencyInvites = useCallback(async (agencyId: string) => {
		const token = sessionStorage.getItem('token');
		const response = await axios.get(`${API_BASE_URL}/api/invitations/${agencyId}/pending-invites`, {
			headers: { Authorization: `Bearer ${token}` }
		});
		return response.data;
	}, []);

	const cancelUserInvite = useCallback(async (inviteId: string) => {
		const token = sessionStorage.getItem('token');
		const response = await axios.post(`${API_BASE_URL}/api/invitations/${inviteId}/cancel`, {}, {
			headers: { Authorization: `Bearer ${token}` }
		});
		return response.data;
	}, []);

	const deleteAgencyUser = useCallback(async (userId: string) => {
		const token = sessionStorage.getItem('token');
		const response = await axios.post(`${API_BASE_URL}/api/agency/${userId}/delete-user`, {
			headers: { Authorization: `Bearer ${token}` }
		});
		return response.data;
	}, []);

	const changePackageSize = useCallback(async (agencyId: string, newSize: number) => {
		const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
		const response = await axios.post(`${API_BASE_URL}/api/agency/${agencyId}/change-package`, { newPackageSize: newSize }, {
			headers: { Authorization: `Bearer ${token}` }
		});
		return response.data;
	}, []);

	const fetchPackagePrices = useCallback(async () => {
		const token = sessionStorage.getItem('token');
		const response = await axios.get(`${API_BASE_URL}/api/agency/get_prices`, {
			headers: { Authorization: `Bearer ${token}` }
		});
		setBaselinePackageCost(response.data.baselinePackageCost);
		setPerSeatCost(response.data.perSeatCost);
		setVatRate(response.data.vatRate);
		return response.data;
	}, []);

	// Fetch agency details
	const fetchAgencyDetails = useCallback(async (agencyId: string) => {
		const token = sessionStorage.getItem('token');
		const response = await axios.get(`${API_BASE_URL}/api/agency/${agencyId}`, {
			headers: { Authorization: `Bearer ${token}` }
		});
		setAgency(response.data);
	}, []);

	// Update payment status after successful payment
	const updatePaymentStatus = useCallback(async () => {
		const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
		const response = await axios.post(`${API_BASE_URL}/api/agency/update-payment-status`, {}, {
			headers: { 
				Authorization: `Bearer ${token}`,
				'Content-Type': 'application/json'
			}
		});
		return response.data;
	}, []);

	return {
		updateUserProfile,
		updateAgencyDetails,
		sendUserInvitation,
		removeAgencyUsers,
		deleteAgencyUser,
		fetchAgencyUsers,
		fetchPendingAgencyInvites,
		cancelUserInvite,
		changePackageSize,
		fetchPackagePrices,
		fetchAgencyDetails,
		updatePaymentStatus,
		baselinePackageCost,
		perSeatCost,
		vatRate,
		agency // Expose agency state
	};
};
